import { FC, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, Link as RouterLink, useParams } from 'react-router';
import { Box, Button, Divider, Grid, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, Favorite } from '@mui/icons-material';
import { marked } from 'marked';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { SpecializationRow, SpecializationRowSkeleton } from '@modules/Specializations/components/SpecializationRow';
import { InfiniteSpecializationsList } from '@modules/Specializations/partials/InfiniteSpecializationsList';
import { SpecializationsList } from '@modules/Specializations/partials/SpecializationsList';
import { InfiniteAssertionsList } from '@modules/Assertions/partials/InfiniteAssertionsList';
import { AnsweredAssertionRow, AnsweredAssertionRowSkeleton } from '@modules/Assertions/components/AnsweredAssertionRow';

import { formatPercentage } from '@shared/helpers/number';
import { ViewHeader } from '@shared/components/ViewHeader';
import { VideoCoverSection } from '@shared/components/VideoCoverSection';

import { useMajor } from '../../hooks/useMajor';
import { useMajorCrud } from '../../hooks/useMajorCrud';
import { ViewMajorSpecialization as ViewMajorSpecializationView } from './views/ViewMajorSpecialization';

interface ViewProps extends Majors.Major {
}

export const ViewMajorView: FC<ViewProps> = major => {
  const router = useRouter();
  const location = useLocation();

  const { likeMajor, unlikeMajor } = useMajorCrud();

  const actionsDisabled = [likeMajor.isPending, unlikeMajor.isPending].some(value => value);

  return (
    <Stack direction="column">
      <Box position="relative" width="100%" overflow="hidden">
        <VideoCoverSection image={major.image} video={major.video} alt={major.title} />
      </Box>
      <Routes>
        <Route path="/" element={(
          <Grid container spacing={2}>
            <Grid size={12}>
              <ViewHeader
                category="Major"
                title={major.title}
                slots={{
                  left: (
                    <IconButton component={RouterLink} to={router.majors.path} color="inherit">
                      <ArrowBack/>
                    </IconButton>
                  ),
                  right: (
                    <Box>
                      {major.match.isFavourite ? (
                        <IconButton onClick={() => unlikeMajor.mutateAsync(major.id)} disabled={actionsDisabled} color="error">
                          <Favorite />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => likeMajor.mutateAsync(major.id)} disabled={actionsDisabled}>
                          <Favorite />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
              <Box px={2} mb={4}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pb={1}>
                  <Typography variant="body2">{formatPercentage(major.match.value)} Match</Typography>
                  <Button component={RouterLink} to={router.majors.view(major.id).assertions.path} color="primary" size="small" variant="text">
                      See Answers
                  </Button>
                </Stack>
                <LinearProgress
                  value={major.match.value * 100}
                  variant="determinate"
                  sx={{
                    height: 10,
                    borderRadius: 1,
                  }}
                />
              </Box>
              {major.description && (
                <Box px={2} mb={3}>
                  <Typography variant="body1" dangerouslySetInnerHTML={{ __html: marked(major.description) }} />
                </Box>
              )}
              {/* SPECIALIZATIONS OFFERED */}
              <Box
                p={2}
                width="100%"
                sx={theme => ({ backgroundColor: theme.palette.tertiary.light })}
              >
                <Typography variant="subtitle1" mb={0.5}>Specializations</Typography>
                <ErrorBoundary>
                  <PermissionedView permission="majors.list">
                    <Paper variant="outlined">
                      <Suspense key={location.key} fallback={(
                        <ListLoading
                          disablePadding
                          repeat={3}
                          renderItem={() => <SpecializationRowSkeleton/>}
                        />
                      )}>
                        <SpecializationsList
                          filters={{
                            major: major.id,
                            limit: 3,
                          }}
                          renderItem={specialization => <SpecializationRow key={specialization.id} {...specialization} linkTo={router.majors.view(major.id).specializations.view(specialization.id).path} />}
                          options={{
                            enablePagination: false,
                          }}
                        />
                      </Suspense>
                      <Divider/>
                      <Button
                        fullWidth
                        component={RouterLink}
                        to={router.majors.view(major.id).specializations.path}
                      >
                        View all
                      </Button>
                    </Paper>
                  </PermissionedView>
                </ErrorBoundary>
              </Box>
            </Grid>
          </Grid>
        )}/>
        <Route
          path="/assertions"
          element={(
            <>
              <ViewHeader
                category="Major"
                title={major.title}
                slots={{
                  left: (
                    <IconButton component={RouterLink} to={router.majors.view(major.id).path} color="primary">
                      <ArrowBack/>
                    </IconButton>
                  ),
                  right: (
                    <Box>
                      {major.match.isFavourite ? (
                        <IconButton onClick={() => unlikeMajor.mutateAsync(major.id)} disabled={actionsDisabled} color="primary">
                          <Favorite />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => likeMajor.mutateAsync(major.id)} disabled={actionsDisabled}>
                          <Favorite />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
              <Box px={2}>
                <Typography variant="subtitle1" mb={0.5}>Answered assertions</Typography>
              </Box>
              <Box>
                <ErrorBoundary>
                  <PermissionedView permission="assertions.list">
                    <Suspense key={location.pathname} fallback={(
                      <ListLoading
                        repeat={3}
                        renderItem={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    )}>
                      <InfiniteAssertionsList
                        filters={{
                          major: major.id,
                          answered: true,
                        }}
                        renderItem={university => <AnsweredAssertionRow key={university.id} {...university} />}
                        renderSkeleton={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    </Suspense>
                  </PermissionedView>
                </ErrorBoundary>
              </Box>
            </>
          )}
        />
        <Route
          path="/specializations"
          element={(
            <Grid container spacing={2}>
              <Grid size={12}>
                <ViewHeader
                  category="Major"
                  title={major.title}
                  slots={{
                    left: (
                      <IconButton component={RouterLink} to={router.majors.view(major.id).path} color="primary">
                        <ArrowBack/>
                      </IconButton>
                    ),
                    right: (
                      <Box>
                        {major.match.isFavourite ? (
                          <IconButton onClick={() => unlikeMajor.mutateAsync(major.id)} disabled={actionsDisabled} color="primary">
                            <Favorite />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => likeMajor.mutateAsync(major.id)} disabled={actionsDisabled}>
                            <Favorite />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  }}
                />
                <Box px={2}>
                  <Typography variant="subtitle1" mb={0.5}>Specializations</Typography>
                </Box>
                <Box>
                  <ErrorBoundary>
                    <PermissionedView permission="majors.list">
                      <Suspense key={location.pathname} fallback={(
                        <ListLoading
                          disablePadding
                          repeat={3}
                          renderItem={() => <SpecializationRowSkeleton/>}
                        />
                      )}>
                        <InfiniteSpecializationsList
                          filters={{
                            major: major.id,
                          }}
                          renderItem={specialization => <SpecializationRow key={major.id} {...specialization} linkTo={router.majors.view(major.id).specializations.view(specialization.id).path} />}
                          renderSkeleton={() => <SpecializationRowSkeleton/>}
                        />
                      </Suspense>
                    </PermissionedView>
                  </ErrorBoundary>
                </Box>
              </Grid>
            </Grid>
          )}
        />
        <Route path="*" element={<Navigate to={router.majors.view(major.id).path} replace />}/>
      </Routes>
    </Stack>
  );
};

export const ViewMajorSpecialization: FC<Majors.Major> = major => {
  const { specializationId = '' } = useParams();

  return (
    <ViewMajorSpecializationView
      id={parseInt(specializationId, 10)}
      major={major}
    />
  );
};

interface Props {
  id: number;
}

export const ViewMajor: FC<Props> = ({ id }) => {

  const router = useRouter();

  const { major } = useMajor({
    id,
  });

  if (!major) {
    return null;
  }

  return (

    <Routes>
      <Route path="/specializations/:specializationId" element={<ViewMajorSpecialization {...major} />} />
      <Route path="/*" element={<ViewMajorView {...major} />}/>
      <Route path="*" element={<Navigate to={router.majors.view(major.id).path} replace />}/>
    </Routes>
  );
};
